import { useEffect, useRef, useState } from 'react';

export interface LiveStatusProps {
  /** What the line says, and what is announced. */
  text: string;
}

/**
 * A status line that is read on screen as well as announced. Mounted empty and given its
 * words only after a rendering update has shown it empty: a status region that reaches the
 * accessibility tree already holding its first text is not reliably announced, the reason
 * Skeleton's, CopyButton's and Toast's live regions are mounted empty too. A fill in the
 * mount's own effect still lands before that update, so it waits two frames.
 *
 * Nor is it filled behind a modal dialog. What a modal covers is inert and out of the
 * accessibility tree, so words given there are never read, and they would already be in
 * place when the modal closed. It waits for that dialog's close instead.
 *
 * A plain span, which takes its look from the element it is set in.
 */
export function LiveStatus({ text }: LiveStatusProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [shown, setShown] = useState('');
  const [, recheck] = useState(0);

  // Without deps: a fill held back by a modal is tried again when that modal closes, or on
  // the next render, whichever comes first.
  useEffect(() => {
    if (shown === text) return;
    const el = ref.current;
    const covering = Array.from(document.querySelectorAll('dialog[open]')).filter((d) => !el || !d.contains(el));
    if (covering.length > 0) {
      const onClose = () => recheck((n) => n + 1);
      for (const d of covering) d.addEventListener('close', onClose);
      return () => {
        for (const d of covering) d.removeEventListener('close', onClose);
      };
    }
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => setShown(text));
    });
    return () => cancelAnimationFrame(frame);
  });

  return (
    <span ref={ref} role="status">
      {shown}
    </span>
  );
}

export default LiveStatus;
